import GlobalLayout from 'layout';
import { GetServerSideProps } from 'next';
import Head from 'next/head';

import HeroResource from 'components/Hero/variants/Resource';

import ComponentGenerator from 'utils/builder/ComponentGenerator';
import { RenderBuilderComponents } from 'utils/builder/RenderBuilderContent';
import { fetchLayoutModels, getPageData } from 'utils/builder/builder.sdk';
import hasSchemaContent from 'utils/schema/hasSchemaContent';

import type { PageProps } from 'types/global';

const MODEL = 'guides';
const PATH = 'guides';
const FIELDS = [
  'id',
  'data.url',
  'data.title',
  'data.summary',
  'data.featuredImage',
  'data.blocks',
  'data.breadcrumbTitle',
  'data.useDefaultHero',
  'data.schemaJson',
].join(',');

const Guides = ({ pageData, layoutData, preview }: PageProps) => {
  const isInBuilderEditor = !!preview && !!pageData?.data?.isInBuilderEditor;
  // Use the value from Builder, default to true only if undefined
  const useDefaultHero = pageData?.data?.useDefaultHero ?? true;

  // Editor-authored HowTo/Article JSON-LD for the guide, rendered SSR.
  // Builder seeds schemaJson as an empty `{}`, so guard on real content.
  const schemaJson = hasSchemaContent(pageData?.data?.schemaJson) ? pageData?.data?.schemaJson : null;

  return (
    <GlobalLayout preview={preview} seoData={pageData.data} {...layoutData}>
      {schemaJson && (
        <Head>
          <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaJson) }} />
        </Head>
      )}
      {/* Render default HeroResource only if useDefaultHero is true */}
      {useDefaultHero && (
        <HeroResource
          {...pageData.data}
          breadcrumbTitle={
            isInBuilderEditor && !pageData?.data?.breadcrumbTitle
              ? pageData?.data?.title
              : pageData?.data?.breadcrumbTitle
          }
        />
      )}

      {/* Render content blocks (can include custom hero when useDefaultHero is false) */}
      <ComponentGenerator>
        <RenderBuilderComponents model={MODEL} content={pageData} />
      </ComponentGenerator>
    </GlobalLayout>
  );
};

export const getServerSideProps: GetServerSideProps = async ({ params, query, res, preview: draftPreview }) => {
  const slug = params?.slug as string;
  // BUILDER=1 is set on the production env too, so it cannot distinguish a real
  // visitor from the Builder.io editor / a shared preview link. The editor iframe
  // and Builder preview links carry `builder.space` / `builder.preview`, so gate
  // includeUnpublished on those: real visitors get published-only content (an
  // unpublished slug 404s instead of leaking) while a preview link renders the draft.
  const isBuilderEditor = !!query['builder.space'] || !!query['builder.preview'];
  const pageData = await getPageData(`${PATH}/${slug}`, MODEL, FIELDS, isBuilderEditor);
  const layoutData = await fetchLayoutModels(isBuilderEditor, !!draftPreview);

  if (!pageData?.data && !isBuilderEditor) {
    return { notFound: true };
  }

  // Edge-cache published content like the catch-all page route: browsers never
  // cache, the Vercel edge serves a shared 5-min snapshot and revalidates in the
  // background. Preview requests are never edge-cached so drafts stay private.
  res.setHeader('Cache-Control', 'private, no-cache, must-revalidate');
  // Draft-preview responses (Next.js Preview Mode) carry unpublished chrome,
  // so they must never seed the shared edge cache either.
  if (!isBuilderEditor && !draftPreview) {
    res.setHeader('CDN-Cache-Control', 'public, s-maxage=300, stale-while-revalidate=300');
  }

  return {
    props: { pageData: pageData || {}, layoutData: layoutData || {}, preview: isBuilderEditor },
  };
};

export default Guides;
